import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Animate from "../Animate";
import useProductForm from "@/hooks/useProductForm";
import {createPortal} from "react-dom";
import { formatMoney } from "@/utils/formatMoney";
import {buyboxGaProps} from "@/utils/ga-ids";
import {product as productStore} from "@/store/Product";
import {price as priceStore} from "@/store/Price";
import {observer} from "mobx-react";
import {fetchAddItem} from "@/services/cart";
import {triggerEvent, registerEvent, unregisterEvent} from "@/utils/custom-events";
import {translateRecharge} from "@/utils/translate-recharge";
import {resolveTierPricing} from "@/utils/tier-pricing";

// Product title/description arrive from Liquid HTML-escaped (e.g. "&amp;"); decode the entities so
// they render as text ("&") instead of showing the raw entity when printed as a React text node.
const decodeEntities = value => {
  if (!value || typeof document === "undefined") return value;
  const el = document.createElement("textarea");
  el.innerHTML = value;
  return el.value;
};

const StickyProductBar = observer(
  ({product, sectionId, image, title, description}) => {
    // only load if the product is available
    const isProductAvailable = product?.available;
    if (!isProductAvailable) return null;

    const {t} = useTranslation();

    const [showProductBar, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    // pdp-v2 (Figma-rebuilt) products only: main-product-v2 renders a `.pdp-v2` root. There the
    // desktop blurb gives way to the subscription-savings line (the v1/OG bar keeps its description).
    const [isPdpV2, setIsPdpV2] = useState(false);

    // Follow in-place product swaps: prefer the currently-shown product published by the v2 buy box,
    // falling back to the section's mount-time prop (v1/OG pages, which don't swap in place).
    const effectiveProduct = productStore.currentProduct ?? product;

    const {hasSubscription, getSellingPlan} = useProductForm({
      product: effectiveProduct,
      formId: sectionId,
    });

    const {variantId: id, sellingPlanId, quantity} = productStore;
    const priceData = priceStore?.currentPrice;

    const sellingPlanOptions = getSellingPlan(sellingPlanId)?.options;
    const sellingPlanLabel = translateRecharge(
      (sellingPlanOptions &&
        (sellingPlanOptions[1]
          ? sellingPlanOptions[1].value
          : sellingPlanOptions[0].value)) ??
        "",
    );

    // Price the SELECTED tier the way the cart will charge it: one-time rows are already line
    // totals, subscription rows are per-unit and get multiplied by the chosen quantity (same rule
    // as ProductFormV2's buy-now subtotal). Previously the bar quoted the per-unit subscription
    // price (38,62 €) while adding two units (77,24 €).
    const tier = resolveTierPricing({
      prices: priceStore?.pricesData,
      variantId: id,
      quantity,
      sellingPlanId,
    });
    const lineTotal = tier.lineTotal ?? priceData?.price ?? 0;

    // "Sie sparen 25 % • Ersparnis 15,44 €": plan discount vs. the one-time price for the same
    // quantity — the numbers the buy-box tiles show. Null for one-time (no line to show).
    const discountMsg =
      tier.isSubscription && tier.percentage > 0 && tier.savings > 0
        ? t("product.sticky_bar.mobile_msg", {
            percentage: tier.percentage,
            savings: formatMoney(tier.savings),
          })
        : null;

    const addToCartLabel = `${t("action.add_to_cart")} • ${formatMoney(lineTotal)} ${hasSubscription ? (sellingPlanId ? `/ ${sellingPlanLabel}` : "") : ""}`;

    const ref = useRef(null);

    const onSubmit = async e => {
      e.preventDefault();
      setLoading(true);

      await fetchAddItem({
        id,
        quantity,
        selling_plan: sellingPlanId,
        // Sticky bar bypasses productStore.addToCart(), so stamp the buy-box attribution here too.
        properties: {
          _buybox_arm: productStore.arm || "unknown",
          _buybox_surface: "sticky",
          _buybox_purchase_type: sellingPlanId ? "subscription" : "one_time",
          _buybox_selected_quantity: String(quantity ?? ""),
          _buybox_selected_frequency: sellingPlanId ? String(productStore.frequency ?? "") : "none",
          // GA4 client_id + session_id so the order webhook can rejoin the
          // purchase to the same GA4 user/session as the buy-box clicks.
          ...buyboxGaProps(),
        },
      });
      triggerEvent("cart:modal:toggle", {
        open: true,
      });
      setLoading(false);
    };

    useEffect(() => {
      setIsPdpV2(!!document.querySelector(".pdp-v2"));
    }, []);

    useEffect(() => {
      if (!("IntersectionObserver" in window)) return undefined;

      let observer = null;

      // Observe the buy-box form + footer; hide the sticky bar while either is on screen.
      const setup = () => {
        if (observer) observer.disconnect();

        const containers = document.querySelectorAll(
          ".shopify-product-form, footer",
        );

        // Track intersection state of all containers
        const intersectionStates = new Map();

        observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              intersectionStates.set(entry.target, entry.isIntersecting);
            });

            // Show sticky bar only when none of the containers are intersecting
            const anyIntersecting = Array.from(
              intersectionStates.values(),
            ).some(isIntersecting => isIntersecting);

            setIsVisible(!anyIntersecting);
          },
          {
            root: null,
            rootMargin: "-10px",
            threshold: [0, 1],
          },
        );

        containers.forEach(container => {
          if (!container) return;
          intersectionStates.set(container, true);
          observer.observe(container);
        });
      };

      setup();

      // The pdp-v2 product toggle swaps the buy-box section in place, replacing
      // .shopify-product-form. The store fires variant:change after the new DOM is
      // mounted, so re-point the observer at the new form then — otherwise the sticky
      // bar keeps watching the detached old form and stops hiding over the buy box.
      registerEvent("variant:change", setup);

      return () => {
        unregisterEvent("variant:change", setup);
        if (observer) observer.disconnect();
      };
    }, []);

    useEffect(() => {
      let chatButtonEl;
      let styleElement;

      const updateChatStyles = (showBar) => {
        // Remove existing style element if it exists
        if (styleElement) {
          styleElement.remove();
        }

        // Create new style element
        styleElement = document.createElement('style');
        styleElement.textContent = `
          #kodif-chat-widget {
            transition: transform 0.3s ease-in-out;
            transform: ${showBar ? 'translateY(-110px) !important' : 'none'};
          }

          #asw-icon {
            transition: transform 0.3s ease-in-out;
            transform: ${showBar ? 'translateY(-100px) !important' : 'none'};
          }

          @media (min-width: 768px) {
            #kodif-chat-widget {
              transform: ${showBar ? 'translateY(-80px) !important' : 'none'};
            }

            #asw-icon {
              transform: ${showBar ? 'translateY(-80px)  !important' : 'none'};
            }
          }

          @media (min-width: 1024px) {
            #kodif-chat-widget {
              transform: ${showBar ? 'translateY(-70px) !important' : 'none'};
            }

            #asw-icon {
              transform: ${showBar ? 'translateY(-70px)  !important' : 'none'};
            }
          }
        `;
        document.head.appendChild(styleElement);
      };

      // Handle initial chat elements position
      updateChatStyles(showProductBar);

      // Handle chat button click events
      const onClickChatButton = () => {
        setTimeout(() => {
          updateChatStyles(showProductBar);
        }, 300);
      };

      const interval = setInterval(() => {
        chatButtonEl = document.querySelector(
          'iframe[id="chat-button"]',
        )?.contentDocument;

        if (chatButtonEl) {
          chatButtonEl?.addEventListener("click", onClickChatButton);
          clearInterval(interval);
        }
      }, 500);

      return () => {
        if (styleElement) {
          styleElement.remove();
        }
        chatButtonEl?.removeEventListener("click", onClickChatButton);
        clearInterval(interval);
      };
    }, [showProductBar]);

    return createPortal(
      <Animate in={showProductBar} nodeRef={ref}>
        <form ref={ref} onSubmit={onSubmit}>
          <div className="fixed bottom-0 z-[8] w-full rounded-t-2xl bg-dark-green p-4 pb-8 text-white md:rounded-t-3xl md:pb-4">
            <div className="mx-auto max-w-[90rem] !px-0 md:flex md:justify-between md:gap-4">
              <div className="hidden gap-4 md:flex">
                {(image || effectiveProduct.featured_image) && (
                  <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-white">
                    <img
                      className="image object-cover"
                      src={image?.src ?? effectiveProduct.featured_image.src}
                      alt={
                        image?.alt ??
                        effectiveProduct.featured_image.alt ??
                        decodeEntities(effectiveProduct.title)
                      }
                    />
                  </div>
                )}

                <div className="flex flex-col">
                  <h3 className="text-2xl font-normal not-italic leading-[120%]">
                    {title ? title : decodeEntities(effectiveProduct.title)}
                  </h3>
                  <div className="line-clamp-1 max-w-[41.5rem] text-ellipsis text-sm font-normal not-italic leading-[140%] tracking-[0.00875rem]">
                    {isPdpV2 && discountMsg
                      ? discountMsg
                      : description
                        ? description
                        : decodeEntities(effectiveProduct.description_stripped)}
                  </div>
                </div>
              </div>

              <button
                disabled={loading}
                className="button button--primary w-full md:w-fit"
              >
                {addToCartLabel}
              </button>
              {discountMsg && (
                <p className="mt-2 flex justify-center text-center text-[13px] font-normal not-italic leading-[130%] text-white md:hidden">
                  {discountMsg}
                </p>
              )}
            </div>
          </div>
        </form>
      </Animate>,

      document.body,
    );
  },
);

export default StickyProductBar;
